import { clearPersistedQueue } from '@/stores/playerQueuePersistence';
import { clearPersistedBulkActionRun } from '@/stores/bulkActionRunStore';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const setAccessToken = (token: string | null): void => {
  if (typeof window === 'undefined') return;
  if (token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }
};

export const setRefreshToken = (token: string | null): void => {
  if (typeof window === 'undefined') return;
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

export const getAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const clearTokens = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  // The play queue is session data and must not outlive the tokens. This is the
  // one chokepoint every session-end passes through — `authStore.logout()`, the
  // 401-refresh-failure branch in `services/http.ts` (which hard-redirects, so
  // no store teardown runs at all), and account deletion. Clearing it in each of
  // those instead would be three call sites that drift; the queue held the
  // previous user's codes, titles and artists, readable by whoever logged in
  // next on the same machine.
  //
  // `playerQueuePersistence` is a leaf module — no store import, no cycle.
  clearPersistedQueue();
  // The bulk-action run is session data for the same reason. A pending run holds the
  // whole selection: the filter, the ticked ids and the lists left out.
  clearPersistedBulkActionRun();

  // NOT-YET-MIGRATED SAVED VIEWS ARE DELIBERATELY LEFT ALONE HERE. They have the
  // same browser-wide, no-user-in-it problem as the queue — an un-migrated set
  // would otherwise be uploaded into whoever logs in next — but this is the wrong
  // place to solve it. `services/http.ts` calls clearTokens() whenever a 401's
  // refresh attempt fails, which is what an ordinary overnight-expired refresh
  // token does, so clearing here destroyed a single user's own views on their own
  // machine with nobody else involved. The ownership question is asked at the
  // upload instead, in `useSavedViewMigrationRunner` — which is also stronger,
  // because it covers a session that expired rather than one that logged out.
};

export const setTokens = (accessToken: string, refreshToken: string): void => {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
};
